<template>
  <div class="gl-tables-page">

    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-branch-header">
        <div>
          <div class="gl-branch-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <router-link to="/owner/branches">My Branches</router-link>
            <span class="gl-bc-sep">/</span>
            <span>Table QR</span>
          </div>
          <h1 class="gl-branch-title">Table QR Codes</h1>
          <p class="gl-branch-sub">
            Print one sheet per table. Scanning it locks the customer's app to dine-in at
            this branch and fills in their table number automatically.
          </p>
        </div>
        <div class="gl-header-actions gl-header-add-btn">
          <button class="gl-ghost-btn" :disabled="!tables.length || printing" @click="printAll">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            {{ printing ? 'Preparing…' : 'Print all' }}
          </button>
          <button class="gl-add-btn" :disabled="!branchId" @click="openGenerate">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add tables
          </button>
        </div>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Branch picker + stats -->
      <div class="gl-stats-strip">
        <div class="gl-branch-select-wrap">
          <label class="gl-stat-lbl">Branch</label>
          <select v-model="branchId" class="gl-branch-select" @change="onBranchChange">
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num">{{ tables.length }}</span>
          <span class="gl-stat-lbl">Tables</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num gl-stat-active">{{ activeCount }}</span>
          <span class="gl-stat-lbl">Active</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num">{{ totalScans }}</span>
          <span class="gl-stat-lbl">Scans</span>
        </div>
        <div class="gl-stat-spacer"></div>
        <div class="gl-search-wrap">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input v-model="search" type="text" class="gl-search-input" placeholder="Search table…">
        </div>
      </div>

      <!-- ── SKELETON ── -->
      <template v-if="loading">
        <div class="gl-table-wrap">
          <div v-for="i in 6" :key="i" class="gl-skel-row" :style="`animation-delay:${i*40}ms`">
            <div class="gl-skel-line w10"></div>
            <div class="gl-skel-line w30"></div>
            <div class="gl-skel-line w15"></div>
            <div class="gl-skel-line w10"></div>
          </div>
        </div>
      </template>

      <!-- ── EMPTY ── -->
      <div v-else-if="filteredTables.length === 0" class="gl-empty">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M14 14h3v3h-3zM18 18h3v3h-3z"/></svg>
        <p>{{ search ? 'No tables match your search.' : 'No tables set up for this branch yet.' }}</p>
        <button v-if="!search && branchId" class="gl-add-btn" style="font-size:13px;padding:8px 16px;border:none" @click="openGenerate">
          Create tables
        </button>
      </div>

      <!-- ── TABLE ── -->
      <template v-else>
        <div class="gl-table-wrap">
          <table class="gl-table">
            <thead>
              <tr>
                <th style="width:76px">QR</th>
                <th>Table</th>
                <th>Code</th>
                <th class="text-center">Scans</th>
                <th class="text-center">Orders</th>
                <th class="text-center">Active</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="table in filteredTables" :key="table.id" class="gl-tbl-row">
                <td>
                  <img v-if="qrImages[table.code]" :src="qrImages[table.code]" class="gl-qr-thumb" :alt="`QR for ${table.display_name}`">
                  <div v-else class="gl-qr-thumb gl-qr-loading"></div>
                </td>
                <td>
                  <div class="gl-tbl-name">{{ table.display_name }}</div>
                  <div class="gl-tbl-url">{{ table.qr_url }}</div>
                </td>
                <td><span class="gl-code">{{ table.code }}</span></td>
                <td class="text-center"><span class="gl-count">{{ table.scan_count || 0 }}</span></td>
                <td class="text-center"><span class="gl-count">{{ table.orders_count || 0 }}</span></td>
                <td class="text-center">
                  <button
                    type="button"
                    :class="['gl-toggle', { on: table.is_active }]"
                    :disabled="togglingId === table.id"
                    :title="table.is_active ? 'Active — click to disable this QR' : 'Disabled — scans are rejected'"
                    @click="toggleActive(table)"
                  >
                    <span class="gl-toggle-knob"></span>
                  </button>
                </td>
                <td>
                  <div class="gl-tbl-actions">
                    <button class="gl-tbl-btn-edit" title="Print this table" @click="printOne(table)">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                    </button>
                    <button class="gl-tbl-btn-edit" title="Download QR image" @click="downloadOne(table)">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    </button>
                    <button class="gl-tbl-btn-warn" title="Issue a new code (invalidates printed sheets)" @click="confirmRegenerate(table)">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                    </button>
                    <button class="gl-tbl-btn-delete" title="Delete" @click="confirmDelete(table)">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

    </div>

    <!-- ── Mobile FAB ── -->
    <button class="gl-mobile-fab" :disabled="!branchId" @click="openGenerate">
      <span class="gl-fab-inner">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Add tables
      </span>
    </button>

    <!-- ── Generate Modal ── -->
    <div v-if="showGenerate" class="gl-modal-backdrop" @click.self="showGenerate = false">
      <div class="gl-modal gl-modal-form">
        <h2 class="gl-modal-title">Add tables</h2>
        <p class="gl-modal-body">
          Creates one QR per table number in the range. Numbers that already exist are skipped.
        </p>

        <div class="gl-form-row">
          <div class="gl-form-field">
            <label>Prefix <span class="gl-optional">optional</span></label>
            <input v-model="genForm.prefix" type="text" maxlength="8" placeholder="e.g. A" class="gl-input">
          </div>
          <div class="gl-form-field">
            <label>From</label>
            <input v-model.number="genForm.from" type="number" min="1" class="gl-input">
          </div>
          <div class="gl-form-field">
            <label>To</label>
            <input v-model.number="genForm.to" type="number" min="1" class="gl-input">
          </div>
        </div>

        <p class="gl-form-preview">
          Creates <strong>{{ generatePreviewCount }}</strong> table(s):
          {{ generatePreviewLabel }}
        </p>

        <p v-if="genError" class="gl-form-error">{{ genError }}</p>

        <div class="gl-modal-actions">
          <button class="gl-modal-cancel" @click="showGenerate = false">Cancel</button>
          <button class="gl-modal-primary" :disabled="generating || generatePreviewCount < 1" @click="generateTables">
            <span v-if="generating" class="spinner-border spinner-border-sm" style="width:13px;height:13px;border-width:2px"></span>
            <span v-else>Create</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Regenerate Modal ── -->
    <div v-if="showRegenerate" class="gl-modal-backdrop" @click.self="showRegenerate = false">
      <div class="gl-modal">
        <div class="gl-modal-icon gl-modal-icon-warn">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
        </div>
        <h2 class="gl-modal-title">Issue a new code?</h2>
        <p class="gl-modal-body">
          <strong>{{ selected?.display_name }}</strong> will get a brand-new QR code.
        </p>
        <div class="gl-modal-blocked">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <span>The sheet currently on this table will stop working. You must reprint it.</span>
        </div>
        <div class="gl-modal-actions">
          <button class="gl-modal-cancel" @click="showRegenerate = false">Cancel</button>
          <button class="gl-modal-primary" :disabled="working" @click="regenerateCode">
            <span v-if="working" class="spinner-border spinner-border-sm" style="width:13px;height:13px;border-width:2px"></span>
            <span v-else>Issue new code</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Delete Modal ── -->
    <div v-if="showDelete" class="gl-modal-backdrop" @click.self="showDelete = false">
      <div class="gl-modal">
        <div class="gl-modal-icon gl-modal-icon-danger">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
        </div>
        <h2 class="gl-modal-title">Delete table</h2>
        <p class="gl-modal-body">
          Delete <strong>{{ selected?.display_name }}</strong>? Its QR stops working immediately.
          Past orders keep their table for reporting.
        </p>
        <div class="gl-modal-actions">
          <button class="gl-modal-cancel" @click="showDelete = false">Cancel</button>
          <button class="gl-modal-confirm" :disabled="working" @click="deleteTable">
            <span v-if="working" class="spinner-border spinner-border-sm" style="width:13px;height:13px;border-width:2px"></span>
            <span v-else>Delete</span>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import QRCode from 'qrcode';
import { ownerBranchApi, ownerTableApi } from '@/services/ownerApi';

export default {
  name: 'TableList',

  data() {
    return {
      loading: true,
      working: false,
      generating: false,
      printing: false,
      togglingId: null,

      branches: [],
      branchId: null,
      tables: [],
      // code -> data URL, so a re-render doesn't redraw every QR from scratch.
      qrImages: {},

      search: '',
      selected: null,
      showGenerate: false,
      showRegenerate: false,
      showDelete: false,

      genForm: { prefix: '', from: 1, to: 10 },
      genError: ''
    };
  },

  computed: {
    currentBranch() {
      return this.branches.find(b => b.id === this.branchId) || null;
    },
    activeCount() {
      return this.tables.filter(t => t.is_active).length;
    },
    totalScans() {
      return this.tables.reduce((sum, t) => sum + (t.scan_count || 0), 0);
    },
    filteredTables() {
      if (!this.search.trim()) return this.tables;
      const q = this.search.trim().toLowerCase();
      return this.tables.filter(t =>
        String(t.table_number).toLowerCase().includes(q) ||
        (t.label || '').toLowerCase().includes(q) ||
        t.code.toLowerCase().includes(q)
      );
    },
    generatePreviewCount() {
      const { from, to } = this.genForm;
      if (!Number.isInteger(from) || !Number.isInteger(to) || to < from || from < 1) return 0;
      return to - from + 1;
    },
    generatePreviewLabel() {
      if (this.generatePreviewCount < 1) return '—';
      const p = this.genForm.prefix || '';
      const { from, to } = this.genForm;
      return this.generatePreviewCount <= 2
        ? `${p}${from}${to !== from ? `, ${p}${to}` : ''}`
        : `${p}${from}, ${p}${from + 1}, … ${p}${to}`;
    }
  },

  mounted() {
    this.fetchBranches();
  },

  methods: {
    async fetchBranches() {
      try {
        const { data } = await ownerBranchApi.list();
        this.branches = data.data || [];
        // Deep-linked from a branch row, otherwise just start on the first branch.
        const routeId = Number(this.$route.params.branchId);
        this.branchId = this.branches.some(b => b.id === routeId)
          ? routeId
          : (this.branches[0]?.id ?? null);
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      }

      if (this.branchId) {
        this.fetchTables();
      } else {
        this.loading = false;
      }
    },

    onBranchChange() {
      this.qrImages = {};
      this.fetchTables();
    },

    async fetchTables() {
      if (!this.branchId) return;
      try {
        this.loading = true;
        const { data } = await ownerTableApi.list(this.branchId);
        this.tables = data.data || [];
        await this.renderQrCodes();
      } catch (error) {
        console.error('Failed to fetch tables:', error);
        this.tables = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * Draw a QR for every table we don't already have one for. Rendered in the
     * browser rather than fetched, so no print run depends on a network image.
     */
    async renderQrCodes(size = 240) {
      const pending = this.tables.filter(t => !this.qrImages[t.code]);
      const drawn = await Promise.all(
        pending.map(t =>
          QRCode.toDataURL(t.qr_url, { width: size, margin: 1, errorCorrectionLevel: 'M' })
            .then(url => [t.code, url])
            .catch(() => null)
        )
      );
      const next = { ...this.qrImages };
      drawn.filter(Boolean).forEach(([code, url]) => { next[code] = url; });
      this.qrImages = next;
    },

    // ── Create ────────────────────────────────────────
    openGenerate() {
      // Default the range to continue after the highest existing number, so the
      // common "we added five more tables" case needs no arithmetic.
      const highest = this.tables
        .map(t => parseInt(String(t.table_number).replace(/\D/g, ''), 10))
        .filter(n => !Number.isNaN(n))
        .reduce((max, n) => Math.max(max, n), 0);

      this.genForm = { prefix: '', from: highest + 1, to: highest + 10 };
      this.genError = '';
      this.showGenerate = true;
    },

    async generateTables() {
      this.genError = '';
      try {
        this.generating = true;
        const { data } = await ownerTableApi.generate(this.branchId, {
          from: this.genForm.from,
          to: this.genForm.to,
          prefix: this.genForm.prefix || undefined
        });
        this.showGenerate = false;
        await this.fetchTables();
        this.$toast?.show?.(data.message);
      } catch (error) {
        this.genError = error.response?.data?.message || 'Failed to create tables.';
      } finally {
        this.generating = false;
      }
    },

    // ── Row actions ───────────────────────────────────
    async toggleActive(table) {
      const next = !table.is_active;
      try {
        this.togglingId = table.id;
        await ownerTableApi.update(table.id, { is_active: next });
        table.is_active = next;
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to update table');
      } finally {
        this.togglingId = null;
      }
    },

    confirmRegenerate(table) {
      this.selected = table;
      this.showRegenerate = true;
    },

    async regenerateCode() {
      if (!this.selected) return;
      try {
        this.working = true;
        await ownerTableApi.regenerateCode(this.selected.id);
        this.showRegenerate = false;
        await this.fetchTables();
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to issue a new code');
      } finally {
        this.working = false;
      }
    },

    confirmDelete(table) {
      this.selected = table;
      this.showDelete = true;
    },

    async deleteTable() {
      if (!this.selected) return;
      try {
        this.working = true;
        await ownerTableApi.delete(this.selected.id);
        this.showDelete = false;
        await this.fetchTables();
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete table');
      } finally {
        this.working = false;
      }
    },

    downloadOne(table) {
      const href = this.qrImages[table.code];
      if (!href) return;
      const a = document.createElement('a');
      a.href = href;
      a.download = `${this.slug(this.currentBranch?.name)}-table-${table.table_number}.png`;
      a.click();
    },

    // ── Printing ──────────────────────────────────────
    printOne(table) {
      this.openPrintWindow([table]);
    },

    async printAll() {
      this.printing = true;
      try {
        await this.renderQrCodes(360); // sharper — these end up on paper
        this.openPrintWindow(this.tables.filter(t => t.is_active));
      } finally {
        this.printing = false;
      }
    },

    /**
     * Print sheet: one card per table. The table number is printed large in plain
     * text as well as encoded — staff need to verify the customer is at the right
     * sheet, and it is the fallback when a camera won't focus.
     */
    openPrintWindow(tables) {
      if (!tables.length) return;

      const branchName = this.escape(this.currentBranch?.name || '');
      const cards = tables.map(t => `
        <div class="card">
          <div class="brand">${branchName}</div>
          <div class="table-no">${this.escape(t.display_name)}</div>
          <img src="${this.qrImages[t.code] || ''}" alt="">
          <div class="cta">Scan to order &amp; pay from your table</div>
          <div class="code">${this.escape(t.code)}</div>
        </div>
      `).join('');

      const win = window.open('', '_blank');
      if (!win) {
        alert('Please allow pop-ups for this site to print QR sheets.');
        return;
      }

      win.document.write(`<!doctype html><html><head><meta charset="utf-8">
        <title>Table QR — ${branchName}</title>
        <style>
          @page { size: A4; margin: 12mm; }
          * { box-sizing: border-box; }
          body { font-family: system-ui, -apple-system, "Segoe UI", sans-serif; margin: 0; }
          .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10mm; }
          .card {
            border: 1.5pt dashed #c9ccd2; border-radius: 6mm; padding: 8mm 6mm;
            text-align: center; break-inside: avoid; page-break-inside: avoid;
          }
          .brand { font-size: 11pt; font-weight: 600; color: #6b7280; letter-spacing: 0.3pt; }
          .table-no { font-size: 26pt; font-weight: 800; color: #111318; margin: 2mm 0 4mm; }
          .card img { width: 46mm; height: 46mm; display: block; margin: 0 auto; }
          .cta { font-size: 10pt; color: #374151; margin-top: 4mm; font-weight: 600; }
          .code { font-size: 8pt; color: #9ca3af; margin-top: 2mm; font-family: ui-monospace, monospace; letter-spacing: 1pt; }
        </style></head>
        <body><div class="grid">${cards}</div></body></html>`);
      win.document.close();
      win.focus();
      // Give the data-URL images a tick to decode, or the sheet prints blank.
      win.setTimeout(() => win.print(), 400);
    },

    // ── Helpers ───────────────────────────────────────
    escape(value) {
      return String(value ?? '').replace(/[&<>"']/g, c => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
    },

    slug(value) {
      return String(value ?? 'branch').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }
  }
};
</script>

<style scoped>
.gl-tables-page { padding-bottom: 48px; }

/* ── Header ── */
.gl-branch-header { display: flex; align-items: flex-end; justify-content: space-between; padding: 28px 0 20px; gap: 16px; }
.gl-branch-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: #9ca3af; margin-bottom: 4px; }
.gl-branch-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-branch-breadcrumb a:hover { color: #900C27; }
.gl-bc-sep { opacity: 0.5; }
.gl-branch-title { font-size: 22px; font-weight: 700; color: #111318; letter-spacing: -0.4px; margin: 0; }
.gl-branch-sub { font-size: 12.5px; color: #9ca3af; margin: 4px 0 0; max-width: 520px; }
.gl-header-actions { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.gl-add-btn {
  display: inline-flex; align-items: center; gap: 7px; padding: 9px 18px; border-radius: 10px; border: none;
  background: #900C27; color: #fff; font-size: 13.5px; font-weight: 600; text-decoration: none; cursor: pointer;
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s; box-shadow: 0 2px 8px rgba(144,12,39,0.25);
}
.gl-add-btn:hover:not(:disabled) { background: #6f0a1e; color: #fff; transform: translateY(-1px); box-shadow: 0 4px 14px rgba(144,12,39,0.30); }
.gl-add-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.gl-ghost-btn {
  display: inline-flex; align-items: center; gap: 7px; padding: 9px 16px; border-radius: 10px;
  background: #fff; border: 1.5px solid #e5e7eb; color: #374151; font-size: 13.5px; font-weight: 600; cursor: pointer;
  transition: all 0.15s;
}
.gl-ghost-btn:hover:not(:disabled) { border-color: #d1d5db; background: #f9fafb; }
.gl-ghost-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Stats strip ── */
.gl-stats-strip {
  display: flex; align-items: center; gap: 14px; background: #fff; border: 1.5px solid #f0f1f3;
  border-radius: 14px; padding: 12px 18px; margin-bottom: 16px;
}
.gl-branch-select-wrap { display: flex; flex-direction: column; gap: 2px; min-width: 180px; }
.gl-branch-select {
  border: none; background: transparent; outline: none; font-size: 14px; font-weight: 700;
  color: #111318; cursor: pointer; padding: 0; max-width: 220px;
}
.gl-stat-chip { display: flex; flex-direction: column; align-items: center; gap: 1px; min-width: 56px; }
.gl-stat-num { font-size: 18px; font-weight: 700; color: #111318; line-height: 1.1; }
.gl-stat-num.gl-stat-active { color: #900C27; }
.gl-stat-lbl { font-size: 10.5px; color: #9ca3af; font-weight: 500; letter-spacing: 0.3px; }
.gl-stat-divider { width: 1px; height: 28px; background: #f0f1f3; flex-shrink: 0; }
.gl-stat-spacer { flex: 1; }
.gl-search-wrap { display: flex; align-items: center; gap: 7px; background: #f3f4f6; border-radius: 9px; padding: 7px 12px; min-width: 200px; }
.gl-search-wrap svg { color: #9ca3af; flex-shrink: 0; }
.gl-search-input { border: none; background: transparent; outline: none; font-size: 13px; color: #374151; width: 100%; }
.gl-search-input::placeholder { color: #9ca3af; }

/* ── Empty ── */
.gl-empty {
  display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 64px 24px; color: #9ca3af;
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px;
}
.gl-empty svg { color: #d1d5db; }
.gl-empty p { font-size: 14px; margin: 0; }

/* ── Table ── */
.gl-table-wrap { background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px; overflow: hidden; margin-bottom: 20px; }
.gl-table { width: 100%; border-collapse: collapse; }
.gl-table thead tr { border-bottom: 1.5px solid #f0f1f3; }
.gl-table th {
  padding: 12px 16px; font-size: 11px; font-weight: 700; color: #9ca3af;
  text-transform: uppercase; letter-spacing: 0.6px; text-align: left; white-space: nowrap;
}
.gl-table th.text-center { text-align: center; }
.gl-tbl-row { border-bottom: 1px solid #f8f9fa; transition: background 0.12s; }
.gl-tbl-row:last-child { border-bottom: none; }
.gl-tbl-row:hover { background: #fafafa; }
.gl-table td { padding: 11px 16px; vertical-align: middle; }
.gl-table td.text-center { text-align: center; }

.gl-qr-thumb { width: 48px; height: 48px; border-radius: 6px; display: block; }
.gl-qr-loading { background: #f0f1f3; }
.gl-tbl-name { font-size: 13.5px; font-weight: 600; color: #111318; }
.gl-tbl-url { font-size: 11px; color: #9ca3af; margin-top: 1px; word-break: break-all; }
.gl-code { font-size: 12px; font-weight: 600; color: #6b7280; background: #f3f4f6; padding: 3px 9px; border-radius: 6px; font-family: ui-monospace, monospace; letter-spacing: 0.5px; }
.gl-count { display: inline-block; min-width: 28px; font-size: 12px; font-weight: 700; color: #6b7280; background: #f3f4f6; padding: 3px 10px; border-radius: 20px; }

.gl-toggle {
  position: relative; width: 40px; height: 22px; border-radius: 20px; border: none;
  background: #d1d5db; cursor: pointer; transition: background 0.18s; padding: 0; vertical-align: middle;
}
.gl-toggle.on { background: #900C27; }
.gl-toggle:disabled { opacity: 0.6; cursor: wait; }
.gl-toggle-knob {
  position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%;
  background: #fff; transition: transform 0.18s; box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}
.gl-toggle.on .gl-toggle-knob { transform: translateX(18px); }

.gl-tbl-actions { display: flex; align-items: center; justify-content: center; gap: 6px; }
.gl-tbl-btn-edit, .gl-tbl-btn-delete, .gl-tbl-btn-warn {
  width: 32px; height: 32px; border-radius: 8px; border: none;
  display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s;
}
.gl-tbl-btn-edit { background: rgba(144,12,39,0.08); color: #900C27; }
.gl-tbl-btn-edit:hover { background: rgba(144,12,39,0.16); }
.gl-tbl-btn-warn { background: rgba(245,158,11,0.10); color: #d97706; }
.gl-tbl-btn-warn:hover { background: rgba(245,158,11,0.20); }
.gl-tbl-btn-delete { background: rgba(239,68,68,0.07); color: #ef4444; }
.gl-tbl-btn-delete:hover { background: rgba(239,68,68,0.14); }

/* ── Skeleton ── */
.gl-skel-row { display: flex; align-items: center; gap: 24px; padding: 16px; border-bottom: 1px solid #f8f9fa; }
.gl-skel-line { height: 12px; border-radius: 6px; background: linear-gradient(90deg, #f0f1f3 25%, #e8e9eb 50%, #f0f1f3 75%); background-size: 200% 100%; animation: gl-skel-wave 1.5s ease infinite; }
.gl-skel-line.w30 { width: 30%; } .gl-skel-line.w15 { width: 15%; } .gl-skel-line.w10 { width: 10%; }
@keyframes gl-skel-wave { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* ── Mobile FAB ── */
.gl-mobile-fab { display: none; position: fixed; bottom: 24px; right: 20px; z-index: 200; border: none; background: transparent; padding: 0; }
.gl-fab-inner {
  display: inline-flex; align-items: center; gap: 8px; padding: 13px 20px; border-radius: 50px;
  background: #900C27; color: #fff; font-size: 14px; font-weight: 700;
  box-shadow: 0 6px 24px rgba(144,12,39,0.40), 0 2px 6px rgba(0,0,0,0.15); transition: transform 0.15s, box-shadow 0.15s;
}
.gl-mobile-fab:hover .gl-fab-inner { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(144,12,39,0.45); }

/* ── Modals ── */
.gl-modal-backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,0.35); display: flex; align-items: center; justify-content: center;
  z-index: 9999; backdrop-filter: blur(4px); animation: gl-backdrop-in 0.15s ease;
}
@keyframes gl-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
.gl-modal {
  background: #fff; border-radius: 20px; padding: 32px 28px; width: 100%; max-width: 400px; margin: 16px; text-align: center;
  animation: gl-modal-in 0.2s cubic-bezier(0.34,1.56,0.64,1); box-shadow: 0 20px 60px rgba(0,0,0,0.15);
}
.gl-modal-form { max-width: 440px; text-align: left; }
@keyframes gl-modal-in { from { opacity: 0; transform: scale(0.92) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
.gl-modal-icon { width: 52px; height: 52px; border-radius: 16px; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; }
.gl-modal-icon-danger { background: rgba(239,68,68,0.10); color: #ef4444; }
.gl-modal-icon-warn { background: rgba(245,158,11,0.12); color: #d97706; }
.gl-modal-title { font-size: 17px; font-weight: 700; color: #111318; margin: 0 0 8px; }
.gl-modal-body { font-size: 13.5px; color: #6b7280; margin: 0 0 16px; line-height: 1.5; }
.gl-modal-body strong { color: #111318; }
.gl-modal-blocked {
  display: flex; align-items: flex-start; gap: 8px; text-align: left; background: #fff8ed; border: 1px solid #fde68a;
  border-radius: 10px; padding: 12px 14px; margin-bottom: 20px; font-size: 13px; color: #92400e; line-height: 1.5;
}
.gl-modal-blocked svg { flex-shrink: 0; margin-top: 1px; color: #f59e0b; }

.gl-form-row { display: flex; gap: 10px; margin-bottom: 12px; }
.gl-form-field { flex: 1; display: flex; flex-direction: column; gap: 5px; }
.gl-form-field label { font-size: 11.5px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.4px; }
.gl-optional { font-weight: 500; text-transform: none; letter-spacing: 0; color: #c4c8d0; }
.gl-input {
  height: 38px; border-radius: 9px; border: 1.5px solid #e5e7eb; padding: 0 11px;
  font-size: 13.5px; color: #111318; outline: none; transition: border-color 0.15s; width: 100%;
}
.gl-input:focus { border-color: #900C27; }
.gl-form-preview { font-size: 12.5px; color: #6b7280; margin: 0 0 16px; }
.gl-form-preview strong { color: #111318; }
.gl-form-error { font-size: 12.5px; color: #ef4444; margin: 0 0 12px; }

.gl-modal-actions { display: flex; gap: 10px; }
.gl-modal-cancel {
  flex: 1; height: 40px; border-radius: 10px; border: 1.5px solid #e5e7eb; background: #fff;
  color: #6b7280; font-size: 13.5px; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.gl-modal-cancel:hover { background: #f9fafb; border-color: #d1d5db; color: #374151; }
.gl-modal-primary, .gl-modal-confirm {
  flex: 1; height: 40px; border-radius: 10px; border: none; color: #fff;
  font-size: 13.5px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; transition: background 0.15s;
}
.gl-modal-primary { background: #900C27; }
.gl-modal-primary:hover:not(:disabled) { background: #6f0a1e; }
.gl-modal-confirm { background: #ef4444; }
.gl-modal-confirm:hover:not(:disabled) { background: #dc2626; }
.gl-modal-primary:disabled, .gl-modal-confirm:disabled { opacity: 0.65; cursor: not-allowed; }

/* ── Mobile responsive ── */
@media (max-width: 767px) {
  .gl-tables-page { padding-bottom: 100px; }
  .gl-branch-header { padding: 14px 0 12px; }
  .gl-branch-title { font-size: 18px; }
  .gl-header-add-btn { display: none !important; }
  .gl-mobile-fab { display: flex; }
  .gl-stats-strip { flex-wrap: wrap; gap: 10px; padding: 10px 14px; }
  .gl-branch-select-wrap { min-width: 100%; }
  .gl-search-wrap { min-width: 100%; order: 5; }
  .gl-table-wrap { overflow-x: auto; }
  .gl-table { min-width: 720px; }
  .gl-modal-backdrop { padding: 68px 16px 16px !important; }
}
</style>

<style>
/* ── Dark mode ── */
body.dark-only .gl-branch-title { color: rgba(255,255,255,0.92) !important; }
body.dark-only .gl-branch-breadcrumb, body.dark-only .gl-branch-breadcrumb a, body.dark-only .gl-branch-sub { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-stats-strip { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-branch-select { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-branch-select option { background: #262932 !important; color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-stat-num { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-stat-lbl { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-stat-divider { background: #3d4255 !important; }
body.dark-only .gl-search-wrap { background: #323644 !important; }
body.dark-only .gl-search-input { color: rgba(255,255,255,0.8) !important; }
body.dark-only .gl-ghost-btn { background: #323644 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.7) !important; }
body.dark-only .gl-table-wrap { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-table th { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-table thead tr { border-bottom-color: #323644 !important; }
body.dark-only .gl-tbl-row { border-bottom-color: #2d3044 !important; }
body.dark-only .gl-tbl-row:hover { background: rgba(144,12,39,0.06) !important; }
body.dark-only .gl-tbl-name { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-tbl-url { color: rgba(255,255,255,0.28) !important; }
body.dark-only .gl-code, body.dark-only .gl-count { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.55) !important; }
body.dark-only .gl-toggle { background: #3d4255 !important; }
body.dark-only .gl-toggle.on { background: #900C27 !important; }
body.dark-only .gl-empty { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-empty p { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-skel-line { background: linear-gradient(90deg, #2d3044 25%, #323644 50%, #2d3044 75%) !important; background-size: 200% 100% !important; }
body.dark-only .gl-modal { background: #262932 !important; }
body.dark-only .gl-modal-title { color: rgba(255,255,255,0.92) !important; }
body.dark-only .gl-modal-body { color: rgba(255,255,255,0.5) !important; }
body.dark-only .gl-modal-body strong { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-modal-cancel { background: #323644 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.55) !important; }
body.dark-only .gl-form-field label { color: rgba(255,255,255,0.45) !important; }
body.dark-only .gl-input { background: #323644 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-form-preview { color: rgba(255,255,255,0.45) !important; }
body.dark-only .gl-form-preview strong { color: rgba(255,255,255,0.87) !important; }
</style>
